import { format } from 'date-fns/esm'
import { useCallback, useEffect, useReducer, useRef } from 'react'

import type { SlotWithLocationAndDoctor } from '../types/booking.type'

/**
 * State machine for accumulating booking slots across filter changes and
 * pagination (load-more) requests.
 *
 * ## States
 *
 * - `idle` — the accumulated slots are up-to-date with the current filters.
 *   New slots arriving in this state are **merged**: every date present in
 *   the batch is replaced by the batch's slots for that date, dates the
 *   batch does not mention are kept. Pagination only brings new dates, so
 *   for it this is a plain append; a single-date refresh (tapping a date
 *   already loaded) brings one date and overwrites it. The result is kept
 *   in start order, so a refreshed date keeps its place in the carousel.
 *
 *   Replacing per date rather than appending with a de-dup is what keeps a
 *   slot from showing twice (AZMM-2746): Synops answers the 30-day search and
 *   the single-day search with different block durations for the same start
 *   time, so the same 14:00 slot came back with another `end` (and id) and
 *   survived a key-based de-dup.
 *
 * - `awaiting-filter-result` — a filter change has been initiated but the API
 *   response hasn't landed yet. The UI should treat the current slots as stale.
 *   The next batch of slots arriving in this state **replaces** the
 *   accumulation entirely instead of appending.
 *
 * ## How it's driven
 *
 * Filter detection is **event-driven**, not reactive: callers invoke
 * `markFiltersChanged()` from the handler that triggers the filter update.
 * This avoids fragile `useEffect` change-detection on serialized filters.
 *
 * Slot processing uses a single `useEffect` watching `stepSlots` and
 * `isLoading` — justified because slot data arrives as props from the parent.
 * The reducer's `status` field tells it whether to replace or append.
 *
 * The effect tracks the previous `stepSlots` reference to distinguish "new
 * slots arrived" from "isLoading toggled with the same slots". Without this,
 * an `isLoading` change would re-feed the old slots into the reducer and
 * prematurely consume the `awaiting-filter-result` state.
 */

type SlotAccumulatorStatus = 'idle' | 'awaiting-filter-result'

type SlotAccumulatorState = {
  slots: SlotWithLocationAndDoctor[]
  selectedDate: string | null
  status: SlotAccumulatorStatus
}

export type SlotAccumulatorAction =
  | { type: 'FILTERS_CHANGED' }
  | { type: 'SLOTS_RECEIVED'; slots: SlotWithLocationAndDoctor[] }
  | { type: 'FILTER_SETTLED_EMPTY' }
  | { type: 'SET_SELECTED_DATE'; date: string }

export const initialSlotAccumulatorState: SlotAccumulatorState = {
  slots: [],
  selectedDate: null,
  status: 'idle',
}
const initialState = initialSlotAccumulatorState

export function slotAccumulatorReducer(
  state: SlotAccumulatorState,
  action: SlotAccumulatorAction,
): SlotAccumulatorState {
  switch (action.type) {
    case 'FILTERS_CHANGED': {
      return { ...state, status: 'awaiting-filter-result' }
    }

    case 'SLOTS_RECEIVED': {
      if (state.status === 'awaiting-filter-result') {
        const slots = dedupeSlots(action.slots)
        return {
          status: 'idle',
          slots,
          selectedDate: getFirstSlotDate(slots),
        }
      }

      // Merge per date: the batch is authoritative for every date it covers.
      const refreshedDates = new Set(action.slots.map(getSlotDate))
      const kept = state.slots.filter((slot) => !refreshedDates.has(getSlotDate(slot)))
      // Re-sort: appending the refreshed date would move it to the end of the
      // carousel, so the selected day jumped out of view and looked
      // deselected (AZMM-2789, AZMM-2792).
      const updatedSlots = sortByStart([...kept, ...dedupeSlots(action.slots)])

      return {
        ...state,
        status: 'idle',
        slots: updatedSlots,
        selectedDate: state.selectedDate ?? (updatedSlots.length > 0 ? getFirstSlotDate(updatedSlots) : null),
      }
    }

    case 'FILTER_SETTLED_EMPTY': {
      return { ...initialState }
    }

    case 'SET_SELECTED_DATE': {
      return { ...state, selectedDate: action.date }
    }

    default: {
      return state
    }
  }
}

// Hook
// -------------------------------------------------------------------------- /

export function useSlotAccumulator(stepSlots: SlotWithLocationAndDoctor[], isLoading: boolean) {
  const [state, dispatch] = useReducer(slotAccumulatorReducer, initialState)
  const previousStepSlotsRef = useRef<SlotWithLocationAndDoctor[] | null>(null)

  // Process incoming slots — reactive because slot data arrives as props.
  // We track the previous stepSlots reference to avoid re-processing
  // unchanged slots when isLoading toggles.
  useEffect(() => {
    const slotsChanged = previousStepSlotsRef.current !== stepSlots
    previousStepSlotsRef.current = stepSlots

    if (slotsChanged && stepSlots.length > 0) {
      dispatch({ type: 'SLOTS_RECEIVED', slots: stepSlots })
    } else if (!isLoading && state.status === 'awaiting-filter-result') {
      dispatch({ type: 'FILTER_SETTLED_EMPTY' })
    }
  }, [stepSlots, isLoading])

  const markFiltersChanged = useCallback(() => {
    dispatch({ type: 'FILTERS_CHANGED' })
  }, [])

  const setSelectedDate = useCallback((date: string) => {
    dispatch({ type: 'SET_SELECTED_DATE', date })
  }, [])

  return {
    accumulatedSlots: state.slots,
    selectedDate: state.selectedDate,
    setSelectedDate,
    isStale: state.status === 'awaiting-filter-result',
    markFiltersChanged,
  }
}

// Helpers
// -------------------------------------------------------------------------- /

/**
 * Identity of a slot as the patient sees it: one agenda, one start time.
 * Deliberately not `slotId`/`end`: Synops hands out different block ids and
 * durations for the same start time depending on the search window.
 */
function getSlotKey(slot: SlotWithLocationAndDoctor) {
  return `${slot.agendaId}-${slot.start}`
}

function getSlotDate(slot: SlotWithLocationAndDoctor) {
  return slot.start.slice(0, 10)
}

/** First occurrence wins; guards against one response listing a start time twice. */
function dedupeSlots(slots: SlotWithLocationAndDoctor[]) {
  const seen = new Set<string>()
  return slots.filter((slot) => {
    const key = getSlotKey(slot)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Chronological order; dates are carousel keys, so this is also the carousel order. */
function sortByStart(slots: SlotWithLocationAndDoctor[]) {
  return [...slots].sort((a, b) => a.start.localeCompare(b.start))
}

function getFirstSlotDate(slots: SlotWithLocationAndDoctor[]): string | null {
  const first = slots.find((slot) => slot.start)
  if (!first) return null
  return format(new Date(first.start), 'yyyy-MM-dd')
}
